import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  TvBadge,
  TvButton,
  TvCard,
  TvCol,
  TvI,
  TvLabel,
  TvNav,
  TvNavItem,
  TvNavLink,
  TvRow,
  TvSmall,
  TvSpinner,
  TvDiv,
  TvH6
} from "@trustvid/design-system";

import TemplateCard from "../../Templates/components/TemplateCard";
import ShopifyCreateOptionsPanel from "./ShopifyCreateOptionsPanel";
import { isItineraryDraftEntryEnabled } from "../../../../constants/campaignFeatureFlags";
import TvPremiumCrown from "../../../../components/saas/TvPremiumCrown";
import SaasDialog from "../../../../components/saas/SaasDialog";
import {
  CREATE_DESIGN_ITINERARY_LABEL,
  CREATE_DESIGN_SMART_CAMPAIGN_LABEL,
  SAAS_CREATE_DESIGN_STARTERS,
} from "../../../../constants/saasPlansCatalog";
import {
  AI_CAMPAIGN_DIALOG_SUBTITLE,
  DESIGNER_AGENT_DIALOG_SUBTITLE,
  DESIGNER_AGENT_DIALOG_TITLE,
} from "../constants/designerAgentPromptGuide";

const TAB_OPTIONS = [
  { key: "blank", label: "Blank" },
  { key: "import", label: "Import JSON" },
  { key: "template", label: "Template" },
  { key: "itinerary", label: CREATE_DESIGN_ITINERARY_LABEL },
  { key: "smartCampaign", label: CREATE_DESIGN_SMART_CAMPAIGN_LABEL },
  // Designer Agent hidden for now.
  // { key: "designerAgent", label: DESIGNER_AGENT_TAB_LABEL },
  { key: "ai", label: "Shopify" }
];

/** Maps dialog tab keys → SAAS_CREATE_DESIGN_STARTERS ids (premium-locked for Free). */
const TAB_TO_STARTER_ID = Object.freeze({
  blank: "blank",
  template: "template",
  import: "import-json",
  itinerary: "itinerary",
  smartCampaign: "ai-campaign",
  designerAgent: "designer-agent",
  ai: "shopify",
});

const PREMIUM_CREATE_TAB_KEYS = new Set(
  SAAS_CREATE_DESIGN_STARTERS.filter((s) => s.premium).map((s) => {
    const entry = Object.entries(TAB_TO_STARTER_ID).find(([, id]) => id === s.id);
    return entry ? entry[0] : null;
  }).filter(Boolean)
);

const hasTemplatePreview = (template) => Boolean(String(template?.thumbnail_url || "").trim());
const ratioPreviewModifier = (ratio) => String(ratio || "16:9").replace(":", "-");

const CreateDesignDialog = ({
  isOpen,
  activeTab,
  onTabChange,
  ratioOptions,
  selectedAspectRatio = "",
  onSelectAspectRatio,
  templates,
  isLoadingTemplates = false,
  onSelectTemplate,
  selectedTemplateId = null,
  shopifyProducts,
  selectedShopifyProductHandle = "",
  onSelectShopifyProductHandle,
  shopifyTemplates,
  selectedShopifyTemplateId = "",
  onSelectShopifyTemplateId,
  shopifyCreateMode = "ai",
  onShopifyCreateModeChange,
  onCreate,
  onCancel,
  importJsonContent = null,
  itineraryContent = null,
  smartCampaignContent = null,
  designerAgentContent = null,
  promptSection = null,
  isCreateDisabled = false,
  isSubmitting = false,
  showShopifyOption = true,
  itineraryEntryEnabled = isItineraryDraftEntryEnabled(),
  smartCampaignEntryEnabled = false,
  designerAgentEntryEnabled = true,
  lockPremiumCreateModes = false,
  showImportJsonTab = true,
  onPremiumLockedTab = null,
}) => {
  const visibleTabOptions = TAB_OPTIONS.filter((tab) => {
    if (tab.key === "import" && !showImportJsonTab) {
      return false;
    }
    if (tab.key === "ai" && !showShopifyOption) {
      return false;
    }
    if (tab.key === "itinerary" && !itineraryEntryEnabled) {
      return false;
    }
    if (tab.key === "smartCampaign" && !smartCampaignEntryEnabled) {
      return false;
    }
    if (tab.key === "designerAgent" && !designerAgentEntryEnabled) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    if (activeTab === "itinerary" && !itineraryEntryEnabled) {
      onTabChange("blank");
    }
    if (activeTab === "smartCampaign" && !smartCampaignEntryEnabled) {
      onTabChange("blank");
    }
    if (activeTab === "designerAgent" && !designerAgentEntryEnabled) {
      onTabChange("blank");
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };

    const handleBackdropClick = (event) => {
      if (event?.target?.classList?.contains("modal-backdrop")) {
        onCancel();
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleEscape);
    }
    document.addEventListener("click", handleBackdropClick);

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("keydown", handleEscape);
      }
      document.removeEventListener("click", handleBackdropClick);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onCancel]);

  const showCreateButton = !["import", "itinerary", "smartCampaign", "designerAgent"].includes(activeTab);

  return (
  <SaasDialog
    isOpen={isOpen}
    onClose={onCancel}
    title="Create Design"
    size="xl"
    className="tv-create-design"
    bodyClassName="tv-create-design__body"
    testId="create-design-modal"
    bodyTestId="create-design-modal-body"
    footerTestId="create-design-footer"
    closeTestId="create-design-close"
    closeLabel="Close Create Design"
    backdrop
    keyboard
    footer={(
      <>
        <TvDiv className="small text-muted">
          Pick a starter and continue editing in the editor.
        </TvDiv>
        <TvDiv className="d-flex align-items-center gap-2">
          <TvButton color="light" className="border px-3 py-2" onClick={onCancel}>
            Cancel
          </TvButton>
          {showCreateButton ? (
            <TvButton
              color="primary"
              className="px-3 py-2"
              disabled={isCreateDisabled}
              onClick={() => onCreate(activeTab === "ai" ? shopifyCreateMode : activeTab)}
              data-testid="entry-create-button"
            >
              {isSubmitting ? "Creating..." : "Create"}
            </TvButton>
          ) : null}
        </TvDiv>
      </>
    )}
  >
      <TvDiv data-testid="create-design-scroll-area" className="tv-create-design__content">
        <TvRow className="g-3">
        <TvCol md={3} lg={2}>
          <TvDiv className="tv-create-design__intro mb-3">
            <TvDiv className="fw-semibold small">What would you like to create?</TvDiv>
            <TvDiv className="text-muted small">
              Choose a starting point from the options below.
            </TvDiv>
          </TvDiv>
          <TvNav vertical pills className="gap-1 create-design-entry-tabs">
            {visibleTabOptions.map((tab) => {
              const isPremiumLocked =
                lockPremiumCreateModes && PREMIUM_CREATE_TAB_KEYS.has(tab.key);
              return (
              <TvNavItem key={tab.key}>
                <TvNavLink
                  href="#"
                  className={`create-design-entry-tab${isPremiumLocked ? " is-locked" : ""}`}
                  active={activeTab === tab.key}
                  data-testid={isPremiumLocked ? `create-tab-locked-${tab.key}` : `create-tab-${tab.key}`}
                  onClick={(event) => {
                    event.preventDefault();
                    if (isPremiumLocked) {
                      if (typeof onPremiumLockedTab === "function") {
                        onPremiumLockedTab("FREE_CREATE_MODE_DENIED");
                      }
                      return;
                    }
                    onTabChange(tab.key);
                  }}
                >
                  <TvDiv className="d-inline-flex align-items-center gap-1">
                    {tab.label}
                    {isPremiumLocked ? (
                      <TvPremiumCrown title={`${tab.label} — Pay as you go`} size={12} />
                    ) : null}
                  </TvDiv>
                </TvNavLink>
              </TvNavItem>
              );
            })}
          </TvNav>
        </TvCol>
        <TvCol md={9} lg={10} className="d-flex flex-column">
          {activeTab === "smartCampaign" ? (
            <TvH6 className="mb-1">{CREATE_DESIGN_SMART_CAMPAIGN_LABEL}</TvH6>
          ) : null}
          {activeTab === "smartCampaign" ? (
            <TvDiv className="text-muted small mb-2" data-testid="ai-campaign-dialog-subtitle">
              {AI_CAMPAIGN_DIALOG_SUBTITLE}
            </TvDiv>
          ) : null}
          {activeTab === "designerAgent" ? (
            <TvH6 className="mb-1">{DESIGNER_AGENT_DIALOG_TITLE}</TvH6>
          ) : null}
          {activeTab === "designerAgent" ? (
            <TvDiv className="text-muted small mb-2" data-testid="designer-agent-dialog-subtitle">
              {DESIGNER_AGENT_DIALOG_SUBTITLE}
            </TvDiv>
          ) : null}
          {activeTab === "itinerary" ? (
            <TvDiv className="text-muted small mb-2">
              Upload a trip PDF or enter a prompt. This creates a travel itinerary video only — intro plus day-by-day scenes.
            </TvDiv>
          ) : null}
          {promptSection}
          {!["smartCampaign", "designerAgent", "itinerary"].includes(activeTab) ? (
            <TvH6 className="mb-3">Videos</TvH6>
          ) : null}

          {activeTab === "blank" ? (
            <TvDiv className="mb-3">
              <TvLabel className="fw-semibold mb-2 d-block">Aspect Ratio</TvLabel>
              <TvDiv className="tv-create-design__ratio-row">
                {ratioOptions.map((option) => {
                  const isSelected = selectedAspectRatio === option.ratio;
                  const ratioKey = ratioPreviewModifier(option.ratio);
                  return (
                    <TvCard
                      key={option.ratio}
                      className={`tv-create-design__ratio-card${isSelected ? " is-selected" : ""}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                      aria-label={`${option.label} ${option.ratio}`}
                      onClick={() => onSelectAspectRatio(option.ratio)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onSelectAspectRatio(option.ratio);
                        }
                      }}
                      data-testid={`entry-ratio-${ratioKey}`}
                    >
                      <TvDiv className="tv-create-design__ratio-stage">
                        {isSelected ? (
                          <TvDiv className="tv-create-design__ratio-check" aria-hidden>
                            <TvI className="bx bx-check" />
                          </TvDiv>
                        ) : null}
                        <TvDiv className={`tv-create-design__ratio-preview tv-create-design__ratio-preview--${ratioKey}`} />
                      </TvDiv>
                      <TvDiv className="tv-create-design__ratio-meta">
                        <TvDiv className="d-flex align-items-center justify-content-between gap-2">
                          <TvDiv className="fw-semibold">{option.label}</TvDiv>
                          <TvBadge color="light" className="text-muted border fw-normal">
                            {option.ratio}
                          </TvBadge>
                        </TvDiv>
                        <TvSmall className={`d-block mt-1 ${isSelected ? "text-primary" : "text-muted"}`}>
                          {isSelected ? "Selected" : "Blank canvas"}
                        </TvSmall>
                      </TvDiv>
                    </TvCard>
                  );
                })}
              </TvDiv>
            </TvDiv>
          ) : null}

          {activeTab === "template" && (
            <TvDiv className="mb-2">
              {isLoadingTemplates ? (
                <TvDiv className="text-center py-4">
                  <TvSpinner color="primary" />
                </TvDiv>
              ) : (
                <TvDiv
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    overflowX: "hidden",
                    paddingBottom: "6px"
                  }}
                >
                  {templates.filter(hasTemplatePreview).map((template) => {
                    const isSelected = String(selectedTemplateId || "") === String(template.id);
                    return (
                      <TvDiv
                        key={template.id}
                        style={{
                          flex: "1 1 230px",
                          minWidth: 0,
                          borderRadius: "12px",
                          border: isSelected ? "2px solid var(--tv-theme-primary)" : "1px solid var(--bs-border-color)",
                          padding: "4px",
                          background: "var(--bs-body-bg)",
                        }}
                      >
                        <TemplateCard
                          template={template}
                          onUseTemplate={() => onSelectTemplate(template.id)}
                          onExportTemplate={() => {}}
                          onDeleteTemplate={() => {}}
                          showActions={false}
                          compact
                          useTemplateDisabled={false}
                        />
                      </TvDiv>
                    );
                  })}
                </TvDiv>
              )}
              {selectedTemplateId ? (
                <TvDiv className="small text-muted mt-1">Template selected.</TvDiv>
              ) : null}
            </TvDiv>
          )}

          {activeTab === "import" ? (
            <TvDiv>
              {importJsonContent}
            </TvDiv>
          ) : null}

          {activeTab === "itinerary" ? (
            <TvDiv>
              {itineraryContent}
            </TvDiv>
          ) : null}

          {activeTab === "smartCampaign" ? (
            <TvDiv>
              {smartCampaignContent}
            </TvDiv>
          ) : null}

          {activeTab === "designerAgent" ? (
            <TvDiv data-testid="create-design-designer-agent-pane">
              {designerAgentContent}
            </TvDiv>
          ) : null}

          {showShopifyOption && activeTab === "ai" && (
            <ShopifyCreateOptionsPanel
              ratioOptions={ratioOptions}
              selectedAspectRatio={selectedAspectRatio}
              onSelectAspectRatio={onSelectAspectRatio}
              shopifyProducts={shopifyProducts}
              selectedShopifyProductHandle={selectedShopifyProductHandle}
              onSelectShopifyProductHandle={onSelectShopifyProductHandle}
              shopifyTemplates={shopifyTemplates}
              selectedShopifyTemplateId={selectedShopifyTemplateId}
              onSelectShopifyTemplateId={onSelectShopifyTemplateId}
              shopifyCreateMode={shopifyCreateMode}
              onShopifyCreateModeChange={onShopifyCreateModeChange}
            />
          )}
        </TvCol>
      </TvRow>
      </TvDiv>
  </SaasDialog>
  );
};

CreateDesignDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  activeTab: PropTypes.oneOf([
    "blank",
    "import",
    "template",
    "itinerary",
    "smartCampaign",
    "designerAgent",
    "ai",
  ]).isRequired,
  onTabChange: PropTypes.func.isRequired,
  ratioOptions: PropTypes.arrayOf(
    PropTypes.shape({
      ratio: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedAspectRatio: PropTypes.string,
  onSelectAspectRatio: PropTypes.func.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoadingTemplates: PropTypes.bool,
  onSelectTemplate: PropTypes.func.isRequired,
  selectedTemplateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  shopifyProducts: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedShopifyProductHandle: PropTypes.string,
  onSelectShopifyProductHandle: PropTypes.func.isRequired,
  shopifyTemplates: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedShopifyTemplateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelectShopifyTemplateId: PropTypes.func.isRequired,
  shopifyCreateMode: PropTypes.oneOf(["blank", "template", "ai"]),
  onShopifyCreateModeChange: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  importJsonContent: PropTypes.node,
  itineraryContent: PropTypes.node,
  smartCampaignContent: PropTypes.node,
  designerAgentContent: PropTypes.node,
  promptSection: PropTypes.node,
  isCreateDisabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  showShopifyOption: PropTypes.bool,
  itineraryEntryEnabled: PropTypes.bool,
  smartCampaignEntryEnabled: PropTypes.bool,
  designerAgentEntryEnabled: PropTypes.bool,
  showImportJsonTab: PropTypes.bool,
  lockPremiumCreateModes: PropTypes.bool,
  onPremiumLockedTab: PropTypes.func,
};

export default CreateDesignDialog;
